import React, { useState } from "react";
import { FormField } from "./types";
import { PreviewGrid } from "./PreviewGrid";
import { AdvancedSearchBar } from "./AdvancedSearchBar";
import { GridViewSwitcher } from "./GridViewSwitcher";
import { useGridView } from "../core/hooks/useGridView";
import { useQuery } from "@tanstack/react-query";
import { api } from '../core/services/api';

interface DataFrameFieldRendererProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  dependencyParams?: Record<string, any>;
}

export function DataFrameFieldRenderer({
  field,
  value,
  onChange,
  dependencyParams = {},
}: DataFrameFieldRendererProps) {
  const [selectedRows, setSelectedRows] = useState<Record<string, any>[]>([]);
  const [searchParams, setSearchParams] = useState<Record<string, string>>(
    field?.searchParams || {}
  );

  const serverSidePagination = Boolean(
    field.dataSource?.isServerSidePagination
  );

  const disableRowSelection = Boolean(field.dataSource?.disableRowSelection);

  const allowMultipleRowSelection = Boolean(
    field.dataSource?.allowMultipleRowSelection
  );

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: dependencyParams?.page_size || 10,
  });

  // Grid view state
  const { view: gridView, setView: setGridView } = useGridView();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dataframe-field', field.name, field.dataSource, dependencyParams, searchParams, pagination],
    queryFn: async () => {
      if (!field.dataSource) return null;
      
      const filteredSearchParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) => value !== "" && value != null)
      );

      const requestBody = {
        system: field.dataSource.system,
        object_type: field.dataSource.objectType,
        action: field.dataSource.action,
        params: {
          ...dependencyParams,
          ...(serverSidePagination ? {
            offset: pagination.pageSize * pagination.pageIndex,
            page_size: pagination.pageSize,
          } : {}),
          ...(Object.keys(filteredSearchParams).length > 0 ? {
            search: filteredSearchParams
          } : {}),
        },
      };

      console.log('DataFrameFieldRenderer - Request body:', requestBody);
      
      const response: any = await api.post('/crud/execute', requestBody);
      // Handle both .data and .result response formats from Orchestration Service
      return response?.data ?? response?.result ?? response;
    },
    enabled: !!field.dataSource,
  });

  const handleRowSelect = (rows: any[]) => {
    setSelectedRows(rows);
    onChange(
      allowMultipleRowSelection
        ? rows?.map((row) => row[field.valueField || ""]) || null
        : rows?.[0]?.[field.valueField || ""] || null
    );
  };

  const handleSearch = (params: Record<string, string>) => {
    setSearchParams(params);
    setSelectedRows([]);
    onChange("");
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0, // Reset to first page when searching
    }));
    
    // Trigger refetch with a small delay to ensure state updates
    setTimeout(() => {
      refetch();
    }, 100);
  };

  const handlePageChange = (newPagination: any) => {
    setSelectedRows([]);
    onChange("");
    setPagination(newPagination);
  };

  // If no data source is configured, show a message
  if (!field.dataSource) {
    return (
      <div style={{
        marginTop: '8px',
        padding: '16px',
        background: 'var(--tag-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        color: 'var(--main-text-secondary)',
        fontSize: '14px'
      }}>
        No data source configured
      </div>
    );
  }

  return (
    <div style={{ marginTop: '8px' }}>
      <AdvancedSearchBar 
        onSearch={handleSearch} 
        searchParameters={searchParams}
        onRefresh={() => refetch()}
        viewSwitcher={
          <GridViewSwitcher 
            view={gridView} 
            onViewChange={setGridView} 
          />
        }
      />
      <PreviewGrid
        columns={field.columnOrder || []}
        data={Array.isArray(data) ? data : []}
        isLoading={isLoading}
        selectedRows={selectedRows}
        onSelectRows={handleRowSelect}
        enableSelection={!disableRowSelection}
        multiRowSelection={allowMultipleRowSelection}
        onPaginationChange={handlePageChange}
        pagination={pagination}
        serverSidePagination={serverSidePagination}
        rowCount={(data as any)?.total_count}
        view={gridView}
        onViewChange={setGridView}
      />
    </div>
  );
}
